import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import UTRModal from '../../Modal';
import SavedListModalActions from '../SavedListModalActions';
import ListPicker from '../ListPicker';
import useListPicker from '../ListPicker/useListPicker';
import { savePlayerToLists } from '../../../actions/saved-lists-actions';
import { showSuccessToast } from '../../../actions/notification-actions';
import { PopupTypes } from '../../../utils/constants';
import { getSavedListDeepLinkPath, emitSavedListsChanged } from '../../../utils/savedLists';
import { buildAddPlayersToast } from '../AddPlayerToListModal/addPlayerToast';
import { handleSaveToListError } from '../savedListsSave';

import styles from './saveToListModal.scss';

// TMS-2146's data-testid contract pins this exact literal (no `modal.` namespace) — the web
// verifier's Playwright selectors depend on it. Intentional deviation from CLAUDE.md rule 4;
// don't "fix" it to match the general convention, it'll break the verifier.
const TESTID_PREFIX = 'save-to-list-modal';
const MODAL_TITLE = 'Add Player to List';
const GENERIC_ERROR_TOAST_MESSAGE = 'We couldn\'t save this player to your lists. Please try again.';

const SaveToListModal = ({ onCloseClick }) => {
    const dispatch = useDispatch();
    const history = useHistory();
    const playerId = useSelector((state) => state.popups.id);
    const playerName = useSelector((state) => state.popups.playerName);
    const initialSavedListIds = useSelector((state) => state.popups.savedListIds);
    const claimedPlayer = useSelector((state) => state.auth?.user?.claimedPlayer);
    const onSuccess = useSelector((state) => state.popups[`${PopupTypes.SAVE_TO_LIST_POPUP}OnSuccess`]);

    const picker = useListPicker({ initialSavedListIds });
    const [isSaving, setIsSaving] = useState(false);

    const handleSave = async () => {
        if (!picker.isPickerFilled || isSaving) {
            return;
        }
        setIsSaving(true);
        picker.setNameError(null);
        const { trimmedNewListName } = picker;
        const listIds = picker.getSaveListIds();
        // The try only wraps the save call itself — anything thrown by the post-success work
        // (closing, toasting, the onSuccess re-fetch) must never be reported to the member as a
        // failed save, which would invite a duplicate re-submit of a save that actually succeeded.
        let saveResult;
        try {
            saveResult = await dispatch(savePlayerToLists({ // NOSONAR — Redux thunk returns a promise
                playerId,
                listIds,
                newListName: trimmedNewListName || null,
            }));
        } catch (error) {
            handleSaveToListError({
                error,
                hasNewListName: trimmedNewListName.length > 0,
                dispatch,
                setNameError: picker.setNameError,
                genericMessage: GENERIC_ERROR_TOAST_MESSAGE,
            });
            // Only reset on failure — on success onCloseClick() below unmounts this component via
            // PopupContainer, so resetting after that point would set state on an unmounted node.
            setIsSaving(false);
            return;
        }

        // The POST is additive-only (no removal semantics), so the modal never reports its own
        // locally-checked ids as the new truth — a member could uncheck an already-saved list
        // here and nothing would actually be removed server-side. onSuccess is a plain signal;
        // the caller re-fetches authoritative saved status (the shared SavedPlayerStatusProvider's
        // handleSaveSuccess → reprobePlayer, or an equivalent per-surface success handler).
        onCloseClick();
        // Tell any locally-cached saved-list view (the hub, a list detail on this same page) to refetch,
        // since this save happened through the global popup and those views can't otherwise know.
        emitSavedListsChanged();
        // "View List" deep-links to a single target list: either the one existing list that was checked,
        // or the brand-new list whose id the save returns as createdListId. A multi-list save has no
        // single target, so it drops the CTA and switches to the "added to N lists" copy. Reuses the
        // wizard's toast builder.
        const createdListId = saveResult?.data?.createdListId;
        const listCount = listIds.length + (trimmedNewListName ? 1 : 0);
        let targetListId = null;
        if (trimmedNewListName) {
            targetListId = createdListId ?? null;
        } else if (listIds.length === 1) {
            [targetListId] = listIds;
        }
        const canViewList = targetListId !== null && targetListId !== undefined && !!claimedPlayer?.playerId;
        const onViewList = canViewList
            ? (closeToast) => {
                // Opened off the profile (search / rankings / events): route to the viewer's own Saved
                // Lists tab with the target list in the URL, so the tab opens List Detail straight from
                // the listid param — the same deterministic /profiles/ deep link as the digest email.
                history.push(getSavedListDeepLinkPath(claimedPlayer, targetListId));
                closeToast();
            }
            : null;
        dispatch(showSuccessToast(buildAddPlayersToast({
            playerCount: 1,
            playerName,
            listCount,
            onViewList,
        })));
        if (onSuccess) {
            onSuccess();
        }
    };

    return (
        <UTRModal
            title={MODAL_TITLE}
            onCloseClick={onCloseClick}
            className={styles.saveToListModal}
            data-testid={TESTID_PREFIX}
        >
            <div className={`${styles.desktopTitle} d-none d-md-block`} data-testid={`${TESTID_PREFIX}.desktop-title`}>
                {MODAL_TITLE}
            </div>
            <ListPicker data-testid={TESTID_PREFIX} {...picker.listPickerProps} />
            <SavedListModalActions
                data-testid={TESTID_PREFIX}
                onCancel={onCloseClick}
                onSave={handleSave}
                saveDisabled={!picker.isPickerFilled || isSaving}
            />
        </UTRModal>
    );
};

SaveToListModal.propTypes = {
    onCloseClick: PropTypes.func.isRequired,
};

export default SaveToListModal;
